/**
 * Molecule Assessment — local file upload (native picker) + guidelines modal
 * Uploads go straight to Uploadcare REST (/base/) — no Uploadcare popup/widget required.
 */
(function (global) {
  'use strict';

  var activeXhrs = {};
  var localPreviewUrls = {};
  var cameraStream = null;

  var ACCEPT =
    'image/png,image/jpeg,image/gif,application/pdf,.png,.jpg,.jpeg,.gif,.pdf';
  var MAX_BYTES = 3145728;
  var UPLOAD_URL = 'https://upload.uploadcare.com/base/';

  function getPubkey() {
    var root = document.getElementById('wm-assessment-app');
    var fromDom = root && root.getAttribute('data-uploadcare-pubkey');
    var o = (global.__wmAssessmentOverrides && global.__wmAssessmentOverrides.api) || {};
    var cfg = (global.wmAssessmentConfig && global.wmAssessmentConfig.api) || {};
    return String(fromDom || o.uploadcarePubkey || cfg.uploadcarePubkey || '').trim();
  }

  function revokeLocalPreview(slot) {
    var url = localPreviewUrls[slot];
    if (url) {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        /* ignore */
      }
      delete localPreviewUrls[slot];
    }
  }

  function abortUpload(slot) {
    var xhr = activeXhrs[slot];
    if (xhr) {
      try {
        xhr.abort();
      } catch (e) {
        /* ignore */
      }
      delete activeXhrs[slot];
    }
  }

  function uploadFile(file, slot, engine) {
    if (!engine || !slot) return;
    if (!file) return;

    if (file.size > MAX_BYTES) {
      engine.setUploadError('File is too large. Max size is 3MB.');
      return;
    }

    var pubkey = getPubkey();
    if (!pubkey) {
      engine.setUploadError('Upload is not configured. Please contact support.');
      return;
    }

    abortUpload(slot);
    revokeLocalPreview(slot);
    engine.setUploadError('');

    var fileName = file.name || 'Uploaded file';
    var isPdf = /pdf/i.test(file.type || '') || /\.pdf$/i.test(fileName);
    var localPreviewUrl = '';
    if (!isPdf && file.type && /^image\//i.test(file.type)) {
      try {
        localPreviewUrl = URL.createObjectURL(file);
        localPreviewUrls[slot] = localPreviewUrl;
      } catch (e) {
        localPreviewUrl = '';
      }
    }

    // Show row + box preview immediately at 0%
    engine.setUploadProgress(slot, {
      name: fileName,
      progress: 0,
      uploading: true,
      cdnUrl: '',
      previewUrl: localPreviewUrl,
      localPreviewUrl: localPreviewUrl,
      uuid: '',
      isPdf: isPdf
    });

    var fd = new FormData();
    fd.append('UPLOADCARE_PUB_KEY', pubkey);
    fd.append('UPLOADCARE_STORE', '1');
    fd.append('file', file, fileName);

    var xhr = new XMLHttpRequest();
    activeXhrs[slot] = xhr;
    var lastPct = -1;
    var lastPaint = 0;

    xhr.upload.onprogress = function (e) {
      if (!e.lengthComputable || e.total <= 0) return;
      // Cap at 99 until server confirms success
      var pct = Math.min(99, Math.round((e.loaded / e.total) * 100));
      var now = Date.now();
      if (pct === lastPct && now - lastPaint < 80) return;
      lastPct = pct;
      lastPaint = now;
      engine.setUploadProgress(slot, {
        name: fileName,
        progress: pct,
        uploading: true,
        localPreviewUrl: localPreviewUrl,
        previewUrl: localPreviewUrl,
        isPdf: isPdf
      });
    };

    xhr.onerror = function () {
      delete activeXhrs[slot];
      revokeLocalPreview(slot);
      if (engine.clearUpload) engine.clearUpload(slot);
      engine.setUploadError('Upload failed. Please try again.');
    };

    xhr.onabort = function () {
      delete activeXhrs[slot];
    };

    xhr.onload = function () {
      delete activeXhrs[slot];
      var text = xhr.responseText || '';
      var data = {};
      if (text) {
        try {
          data = JSON.parse(text);
        } catch (e) {
          data = { raw: text };
        }
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        var msg =
          (data && (data.detail || data.error || data.message || data.raw)) ||
          'Upload failed. Please try again.';
        revokeLocalPreview(slot);
        if (engine.clearUpload) engine.clearUpload(slot);
        engine.setUploadError(typeof msg === 'string' ? msg : 'Upload failed. Please try again.');
        return;
      }
      var uuid = data && data.file;
      if (!uuid) {
        revokeLocalPreview(slot);
        if (engine.clearUpload) engine.clearUpload(slot);
        engine.setUploadError('Upload failed. Please try again.');
        return;
      }
      var cdnUrl = 'https://ucarecdn.com/' + uuid + '/';
      engine.setUploadProgress(slot, {
        name: fileName,
        progress: 100,
        uploading: false,
        localPreviewUrl: localPreviewUrl,
        previewUrl: localPreviewUrl || cdnUrl
      });
      engine.setUpload(slot, {
        name: fileName,
        cdnUrl: cdnUrl,
        previewUrl: cdnUrl,
        localPreviewUrl: localPreviewUrl,
        uuid: uuid,
        isPdf: isPdf,
        progress: 100,
        uploading: false
      });
    };

    xhr.open('POST', UPLOAD_URL);
    xhr.send(fd);
  }

  var pickerSlot = '';
  var pickerEngine = null;
  var pickerInputs = {};

  function onPickerChange(e) {
    var input = e.target;
    var file = input && input.files && input.files[0];
    if (input) input.value = '';
    if (!file || !pickerSlot || !pickerEngine) return;
    uploadFile(file, pickerSlot, pickerEngine);
  }

  function getPickerInput(opts) {
    opts = opts || {};
    var capture = !!opts.capture;
    var key = capture ? 'camera' : 'gallery';
    if (pickerInputs[key]) return pickerInputs[key];
    var input = document.createElement('input');
    input.type = 'file';
    input.id = 'wma-file-picker-' + key;
    input.accept =
      opts.accept ||
      (capture
        ? 'image/*'
        : 'image/png,image/jpeg,image/gif,.png,.jpg,.jpeg,.gif');
    if (capture) input.setAttribute('capture', 'environment');
    input.setAttribute('aria-hidden', 'true');
    input.tabIndex = -1;
    input.style.cssText =
      'position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;overflow:hidden;z-index:-1;';
    input.addEventListener('change', onPickerChange);
    document.body.appendChild(input);
    pickerInputs[key] = input;
    return input;
  }

  function stopCamera() {
    if (cameraStream) {
      try {
        cameraStream.getTracks().forEach(function (t) {
          t.stop();
        });
      } catch (e) {
        /* ignore */
      }
      cameraStream = null;
    }
    var video = document.querySelector('[data-camera-preview]');
    if (video) {
      try {
        video.srcObject = null;
      } catch (e2) {
        /* ignore */
      }
    }
  }

  function startCamera(videoEl) {
    stopCamera();
    if (!videoEl || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      return Promise.reject(new Error('Camera is not available in this browser.'));
    }
    return navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 1920 }
        }
      })
      .catch(function () {
        return navigator.mediaDevices.getUserMedia({
          audio: false,
          video: true
        });
      })
      .then(function (stream) {
        if (!stream || typeof stream.getTracks !== 'function') {
          throw new Error('Camera is not available in this browser.');
        }
        cameraStream = stream;
        try {
          videoEl.srcObject = stream;
        } catch (err) {
          stopCamera();
          throw err;
        }
        var play = videoEl.play();
        if (play && typeof play.catch === 'function') play.catch(function () {});
        return stream;
      });
  }

  function captureCameraFile(videoEl) {
    return new Promise(function (resolve, reject) {
      if (!videoEl || !videoEl.videoWidth) {
        reject(new Error('Camera is not ready yet. Please wait a moment.'));
        return;
      }
      var canvas = document.createElement('canvas');
      canvas.width = videoEl.videoWidth;
      canvas.height = videoEl.videoHeight;
      var ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Unable to capture photo.'));
        return;
      }
      ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        function (blob) {
          if (!blob) {
            reject(new Error('Unable to capture photo.'));
            return;
          }
          var file;
          try {
            file = new File([blob], 'fullbody-photo.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
          } catch (e) {
            blob.name = 'fullbody-photo.jpg';
            file = blob;
          }
          resolve(file);
        },
        'image/jpeg',
        0.92
      );
    });
  }

  /** Opens native camera or gallery picker — no Uploadcare modal. */
  function openUploader(slot, engine, opts) {
    opts = opts || {};
    pickerSlot = slot || '';
    pickerEngine = engine || pickerEngine;
    if (!pickerSlot) {
      if (engine) engine.setUploadError('Upload slot missing. Please refresh and try again.');
      return;
    }
    if (engine) engine.setUploadError('');

    // Proof / legacy path: prefer in-page labeled input when present.
    // Camera/gallery actions pass forcePicker so they always use the hidden input.
    if (!opts.capture && !opts.forcePicker) {
      var labeled =
        document.getElementById('wma-file-' + pickerSlot) ||
        document.querySelector('[data-upload-input][data-upload-key="' + pickerSlot + '"]');
      if (labeled) {
        try {
          labeled.click();
          return;
        } catch (err) {
          console.warn('[wm-assessment] Labeled file input failed', err);
        }
      }
    }

    try {
      getPickerInput(opts).click();
    } catch (err) {
      console.warn('[wm-assessment] Native file picker failed', err);
      if (engine) engine.setUploadError('Unable to open file picker. Please try again.');
    }
  }

  function bindOnce() {
    /* no-op: REST path does not need Uploadcare widget binding */
  }

  function guidelinesHtml(key, rootEl) {
    var UI = global.WmAssessmentUI;
    var esc = UI.esc;

    // Figma 445:8274 — examples overlay (close above card, no title/footer)
    if (key === 'fullbody') {
      var closeSrc =
        (rootEl && rootEl.getAttribute('data-icon-modal-close')) ||
        '/assets/wma-icon-modal-close.svg';
      return (
        '<div class="wma__modal wma__modal--examples" role="dialog" aria-modal="true" aria-label="Upload requirements">' +
        '<button type="button" class="wma__modal-backdrop" data-action="close-guidelines" aria-label="Close"></button>' +
        '<div class="wma__modal-stack">' +
        '<button type="button" class="wma__modal-close" data-action="close-guidelines" aria-label="Close">' +
        '<img class="wma__icon-img wma__icon-img--modal-close" src="' +
        esc(closeSrc) +
        '" alt="" width="40" height="40" decoding="async">' +
        '</button>' +
        '<div class="wma__modal-panel wma__modal-panel--examples">' +
        UI.photoExamples(rootEl) +
        '</div></div></div>'
      );
    }

    var title = 'Photo guidelines';
    var body = '';
    if (key === 'identity') {
      title = 'Photo ID examples';
      body =
        '<div class="wma__examples">' +
        example(rootEl, 'guidelineDl', 'Driving licence') +
        example(rootEl, 'guidelinePassport', 'Passport') +
        '</div>';
    } else if (key === 'prescription') {
      title = 'Prescription example';
      body =
        '<div class="wma__examples">' +
        example(rootEl, 'guidelinePrescription', 'Clear prescription photo') +
        '</div>';
    }
    return (
      '<div class="wma__modal" role="dialog" aria-modal="true" aria-labelledby="wma-guidelines-title">' +
      '<button type="button" class="wma__modal-backdrop" data-action="close-guidelines" aria-label="Close"></button>' +
      '<div class="wma__modal-panel">' +
      '<h2 id="wma-guidelines-title" class="wma__modal-title">' +
      esc(title) +
      '</h2>' +
      body +
      '<button type="button" class="wma__btn wma__btn--secondary" data-action="close-guidelines">Back to Upload</button>' +
      '</div></div>'
    );
  }

  function example(rootEl, dataKey, caption) {
    var UI = global.WmAssessmentUI;
    var map = {
      guidelineFbDo: 'data-guideline-fb-do',
      guidelineFbDontBaggy: 'data-guideline-fb-dont-baggy',
      guidelineFbDontFace: 'data-guideline-fb-dont-face',
      guidelineFbDontBody: 'data-guideline-fb-dont-body',
      guidelineDl: 'data-guideline-dl',
      guidelinePassport: 'data-guideline-passport',
      guidelinePrescription: 'data-guideline-prescription'
    };
    var attr = map[dataKey];
    var src = (rootEl && attr && rootEl.getAttribute(attr)) || '';
    return (
      '<div class="wma__example">' +
      (src
        ? '<img src="' + UI.esc(src) + '" alt="' + UI.esc(caption) + '" width="161" height="180">'
        : '<div style="height:180px;background:#f8f8f8;border-radius:8px"></div>') +
      '<span>' +
      UI.esc(caption) +
      '</span></div>'
    );
  }

  function rangeConfirmModal(opts) {
    var UI = global.WmAssessmentUI;
    var esc = UI.esc;
    return (
      '<div class="wma__modal wma__modal--range" role="dialog" aria-modal="true" aria-labelledby="wma-range-title">' +
      '<button type="button" class="wma__modal-backdrop" data-action="close-range" aria-label="Close"></button>' +
      '<div class="wma__modal-panel wma__range-panel">' +
      '<div class="wma__range-body">' +
      '<div class="wma__range-card">' +
      '<p class="wma__range-card__label">' +
      esc(opts.kindLabel) +
      '</p>' +
      '<p class="wma__range-card__value">' +
      esc(opts.primary) +
      '</p>' +
      '<p class="wma__range-card__secondary">' +
      esc(opts.secondary) +
      '</p></div>' +
      '<p id="wma-range-title" class="wma__range-title">' +
      esc(opts.title) +
      '</p></div>' +
      '<div class="wma__info-row wma__info-row--range">' +
      UI.INFO_ICON +
      '<span>' +
      esc(opts.info) +
      '</span></div>' +
      '<div class="wma__modal-actions wma__modal-actions--range">' +
      '<button type="button" class="wma__btn wma__btn--beige" data-action="close-range">Edit</button>' +
      '<button type="button" class="wma__btn wm-btn wm-btn--primary wm-btn--lg" data-action="confirm-range">Confirm</button>' +
      '</div></div></div>'
    );
  }

  global.WmAssessmentUploads = {
    ACCEPT: ACCEPT,
    uploadFile: uploadFile,
    abortUpload: abortUpload,
    revokeLocalPreview: revokeLocalPreview,
    openUploader: openUploader,
    startCamera: startCamera,
    stopCamera: stopCamera,
    captureCameraFile: captureCameraFile,
    bindOnce: bindOnce,
    guidelinesHtml: guidelinesHtml,
    rangeConfirmModal: rangeConfirmModal
  };
})(window);
